import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ShoppingBag, Calendar, UtensilsCrossed, Globe } from "lucide-react";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n";

const links = [
  { id: "menu", labelKey: "nav_menu" },
  { id: "piatti", labelKey: "nav_categorie" },
  { id: "chi-siamo", labelKey: "nav_chi_siamo" },
  { id: "dove-siamo", labelKey: "nav_dove_siamo" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { totals, setOpen: setCartOpen } = useCart();
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/5" : "bg-black/30 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="group shrink-0" data-testid="navbar-logo">
          <Logo size={40} />
        </button>

        <nav className="hidden xl:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="relative text-sm tracking-wide text-white/80 hover:text-white transition-colors duration-300
                after:content-[''] after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-gold
                hover:after:w-full after:transition-[width] after:duration-300"
            >
              {t(l.labelKey)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-2.5">
          {/* Language switcher — visible on desktop AND mobile */}
          <button
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            data-testid="lang-switch"
            aria-label="Switch language"
            className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-gold border border-white/10 hover:border-gold/40 px-2.5 md:px-3 py-2 rounded-full transition-colors uppercase font-medium tracking-wider"
          >
            <Globe size={12} /> {lang === "it" ? "EN" : "IT"}
          </button>

          <button
            onClick={() => scrollTo("menu")}
            data-testid="navbar-cta-order"
            className="hidden sm:inline-flex items-center gap-2 border border-gold/50 hover:bg-gold hover:text-ink text-gold text-sm font-medium px-4 md:px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <UtensilsCrossed size={14} /> {t("nav_ordina")}
          </button>

          <button
            data-testid="navbar-cta-book"
            onClick={() => scrollTo("prenota")}
            className="hidden sm:inline-flex items-center gap-2 bg-ember hover:bg-emberDeep text-white text-sm font-medium px-4 md:px-5 py-2.5 rounded-full transition-colors duration-300"
          >
            <Calendar size={14} /> {t("nav_prenota")}
          </button>

          <button
            onClick={() => setCartOpen(true)}
            data-testid="navbar-cart-btn"
            aria-label="Apri carrello"
            className="relative w-10 h-10 rounded-full grid place-items-center bg-ash/60 border border-white/10 hover:border-gold text-white/85 hover:text-white transition-colors"
          >
            <ShoppingBag size={16} />
            {totals.count > 0 && (
              <span className="absolute -top-1 -right-1 bg-ember text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center border border-ink" data-testid="navbar-cart-count">
                {totals.count}
              </span>
            )}
          </button>

          <button className="xl:hidden text-white p-2" onClick={() => setOpen((v) => !v)} data-testid="navbar-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-black/95 backdrop-blur-xl border-t border-white/5" data-testid="navbar-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-left text-white/85 hover:text-gold py-2" data-testid={`nav-mobile-link-${l.id}`}>
                {t(l.labelKey)}
              </button>
            ))}
            <button
              onClick={() => { setLang(lang === "it" ? "en" : "it"); setOpen(false); }}
              className="text-left text-gold py-2 inline-flex items-center gap-2"
              data-testid="nav-mobile-lang"
            >
              <Globe size={14} /> {lang === "it" ? "English" : "Italiano"}
            </button>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button onClick={() => scrollTo("menu")} className="bg-gold/90 hover:bg-gold text-ink px-4 py-3 rounded-full font-medium text-sm" data-testid="nav-mobile-cta-order">
                {t("nav_ordina")}
              </button>
              <button onClick={() => scrollTo("prenota")} className="bg-ember hover:bg-emberDeep text-white px-4 py-3 rounded-full font-medium text-sm" data-testid="nav-mobile-cta-book">
                {t("nav_prenota")}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
}
